import React, {useContext, useEffect} from 'react'
import {
    Box,
    Button,
    Checkbox,
    FormControl,
    FormControlLabel,
    FormGroup,
    FormHelperText,
    FormLabel,
    Grid,
    Link,
    List,
    ListItem,
    Stack,
    TextField,
    Typography,
} from '@mui/material'
import {Trans} from 'react-i18next'
import {CancelButton, PreviousButton} from '../StepperButtons'
import {AppContext} from '../../contexts/AppContextProvider'
import {useForm} from '../../hooks/useForm'
import {CustomerAddress, Product, ShippingInfoState} from '../../contexts/types'
import ShippingInfoForm from './components/ShippingInfoForm'
import * as yup from 'yup'
import {useFormik} from 'formik';
import AddressSelector from "./components/AddressSelector";
import {OrgCustomizationContext} from "../../themes/OrgCustomizationProvider";
import {setShippingInfo} from "../../contexts/ShippingInfoReducer";
import UseTheHackiestPageNavigation from "../../hooks/useTheHackiestPageNavigation";
import useRedirect from "../../hooks/useRedirect";

const ShippingAddress: React.FC = () => {
    const {navigateForward} = UseTheHackiestPageNavigation()
    const {complaint, customer, dispatch, loggedIn, postNotification, clearNotification} =
        useContext(AppContext)
    const {t, tGetParameter, tns} = useContext(OrgCustomizationContext)

    const required = {value: true, message: t('this_field_is_required')}
    const maxChar = (value: number): string => t('gi_limit_of', {characters: value.toString()})
    const {redirect} = useRedirect()
    
    useEffect(() => {
        if (!(complaint.guestInfo.customerNumber || customer.customer)) {
            redirect('login')
        }
    }, [complaint])

    const {
        data,
        errors,
        handleChangeEvent,
        handleChange,
        validateFields,
        enableOnChangeValidation,
    } = useForm<ShippingInfoState>({
        initialValues: {...complaint.shippingInfo},
        validations: {
            signature: {
                required: {
                    value: (value) => !!value.toString().trim(),
                    message: t('this_field_is_required'),
                },
            },
            hasAcceptedTerms: {required},
            shippingAddress: {
                required: {
                    value: (value) => {
                        if ( (complaint.generalComplaint.isReplacementNeeded === "NO") && !complaint.replacements.selectedSecondaryDeviceReplacement) return true;
                        if (!loggedIn) return true

                        const hasReturnToShip = !!((complaint.replacements.selectedMainProductReplacement?.productId || complaint.replacements.selectedSecondaryDeviceReplacement?.productId))

                        return hasReturnToShip
                            ? !!(value as CustomerAddress)?.customer
                            : true
                    },
                    message: t('this_field_is_required'),
                }
            }
        },
    })

    const unauthenticatedValidationSchema = yup.object({
        street: yup
            .string()
            .required(required.message)
            .max(60, maxChar(60)),
        city: yup
            .string()
            .required(required.message)
            .max(40, maxChar(40)),
        region: yup
            .string()
            .max(20, maxChar(20)),
        postalCode: yup
            .string()
            .max(10, maxChar(10)),
        country: yup
            .string()
            .required(required.message)
    })

    const navigateToNextScreen = () => navigateForward()

    const isFormValid = (): boolean => {
        // Activate field level validation tracking
        enableOnChangeValidation(true)

        // // Stop processing if the form has invalid fields
        if (!validateFields(data)) {
            postNotification('error', t('error_mess'))
            return false
        }

        // // Form is valid, proceed with processing

        // // Close the alert message in case it is open
        clearNotification()
        return true
    }


    const handleAuthenticatedSubmit = () => {
        if (!isFormValid()) return
        dispatch(setShippingInfo({...data, customer}))
        navigateToNextScreen()
    }

    const formik = useFormik({
        initialValues: {
            street: complaint.shippingInfo.shippingAddress?.street || '',
            city: complaint.shippingInfo.shippingAddress?.city || '',
            region: complaint.shippingInfo.shippingAddress?.region || '',
            postalCode: complaint.shippingInfo.shippingAddress?.postalCode || '',
            country: complaint.shippingInfo.shippingAddress?.country || complaint.guestInfo.country || ''
        },
        validationSchema: unauthenticatedValidationSchema,
        onSubmit: (values) => {
            if (!isFormValid()) return

            const shippingInfo: ShippingInfoState = {
                ...data,
                customer,
                shippingAddress: {
                    customer: complaint.shippingInfo.customer?.customer as string,
                    salesOrg: '',
                    partner: 'Ship-ToParty',
                    country: values.country,
                    name1: customer.name1,
                    name2: '',
                    city: values.city,
                    postalCode: values.postalCode,
                    region: values.region,
                    street: values.street,
                    telephone: complaint.guestInfo.telephone
                },
            }

            dispatch(setShippingInfo(shippingInfo))

            navigateToNextScreen()
        },
    })

    const getProductDisplayString = (p: Product | undefined) => {
        if (!p) return undefined
        if (p.productId && p.shortDescription) return `${p.productId} - ${p.shortDescription}`
        return p.productId || p.shortDescription
    }

    const getProductDisplay = (p: Product | undefined) => p?.productId || p?.productName
        ? <ListItem sx={{display: 'list-item'}}>{getProductDisplayString(p)}</ListItem>
        : null


    return (
        <form onSubmit={formik.handleSubmit}>
            <Grid container>
                <Stack spacing={2} sx={{width: '100%'}}>
                    <Typography variant='h5'>{t('sa_acknowledgement')}</Typography>

                    <Typography variant='body1'>
                        <List>
                            {getProductDisplay(complaint.generalComplaint.selectedProduct)}
                            {getProductDisplay(complaint.secondaryDevice.selectedProduct)}
                        </List>
                    </Typography>

                    {
                        !loggedIn
                            ? <ShippingInfoForm formik={formik}/>
                            : null
                    }
                    {(((complaint.generalComplaint.isReplacementNeeded === "YES") &&
                        complaint.replacements.selectedMainProductReplacement) || 
                        (complaint.secondaryDevice.isReplacementNeeded && complaint.replacements
                            .selectedSecondaryDeviceReplacement)) && (
                        <Typography variant='h6' sx={{pt: 1}}>
                            {t('sa_shipping_address_page_title')}:
                        </Typography>
                    )}
                    <Typography component='div' variant='body1'>
                        {(complaint.generalComplaint.isReplacementNeeded === "YES") && complaint.replacements.selectedMainProductReplacement && (
                            <p>
                                {getProductDisplayString(complaint.replacements.selectedMainProductReplacement)}
                            </p>
                        )}
                        {complaint.secondaryDevice.isReplacementNeeded && complaint.replacements.selectedSecondaryDeviceReplacement && (
                            <p>
                                {getProductDisplayString(complaint.replacements.selectedSecondaryDeviceReplacement)}
                            </p>
                        )}
                    </Typography>

                    {
                        (loggedIn && (complaint.generalComplaint.isReplacementNeeded === "YES") && complaint.replacements.selectedMainProductReplacement)
                        || (loggedIn && complaint.secondaryDevice.isReplacementNeeded && complaint.replacements.selectedSecondaryDeviceReplacement)
                            ? (
                                <>
                                    <Stack spacing={1} sx={{pb: 4}}>
                                        <Typography variant='h6' color='secondary.contrastText'>
                                            {t('sa_shipping_address')}
                                        </Typography>

                                        <AddressSelector partnerAddressType={['Ship-ToParty']}
                                                         value={data.shippingAddress}
                                                         onChange={a => handleChange('shippingAddress', a as object)}/>
                                        {
                                            (typeof errors.shippingAddress !== 'undefined') &&
                                            <Box sx={{marginLeft: 14, marginTop: 3}}>
                                                <FormHelperText error={true}>{errors.shippingAddress}</FormHelperText>
                                            </Box>
                                        }
                                    </Stack>
                                </>
                            )
                            : null
                    }
                </Stack>

                <Stack spacing={4} sx={{width: '100%'}}>
                    <FormControl sx={{pt: 0}}>
                        <Typography
                            variant='body1'
                            color='secondary.contrastText'
                            sx={{pt: 2}}
                        >
                            {t('sa_label_terms_conditions')}:
                        </Typography>
                        <FormGroup>
                            <FormControlLabel
                                style={{display: 'table'}}
                                control={
                                    <div style={{display: 'table-cell'}}>
                                        <Checkbox
                                            color='primary'
                                            checked={data.hasAcceptedTerms}
                                            onChange={(e) => {
                                                handleChange(
                                                    'hasAcceptedTerms',
                                                    !data.hasAcceptedTerms
                                                )
                                            }}
                                        />
                                    </div>
                                }
                                label={
                                    // @ts-ignore-next-line
                                    <Trans i18nKey={tns('sa_terms_conditions')}>
                                        -
                                        <Link
                                            href={tGetParameter('sa_terms_conditions', '1') || '#'}
                                            sx={{color: 'primary.main'}}
                                            target='_blank'
                                        >
                                            -
                                        </Link>
                                        -
                                    </Trans>
                                }
                            />
                            <FormHelperText error={!!errors.hasAcceptedTerms}>
                                {errors.hasAcceptedTerms}
                            </FormHelperText>
                        </FormGroup>
                    </FormControl>

                    <FormControl sx={{mb: 2}}>
                        <FormLabel sx={{mb: 1}}>{t('sa_signature')}:</FormLabel>
                        <TextField
                            id='signature'
                            size='small'
                            sx={{mb: 3, mr: 'auto'}}
                            error={typeof errors.signature !== 'undefined'}
                            helperText={errors.signature}
                            value={data.signature}
                            onChange={handleChangeEvent('signature')}
                        />
                    </FormControl>
                </Stack>
                <Stack
                    direction='row-reverse'
                    spacing={2}
                    sx={{pt: 4, width: '100%'}}
                >

                    <Button
                        variant='contained'
                        color='primary'
                        type={!loggedIn ? 'submit' : 'button'} // IF USER ISNT LOGGED IN ( SUBMITS WITH FORMIK ) ELSE USES USEFORM
                        onClick={(e) => loggedIn && handleAuthenticatedSubmit()}
                    >
                        {t('finish_complaint')}
                    </Button>

                    <PreviousButton before={() => {
                        dispatch(setShippingInfo(loggedIn
                            ? data
                            : {
                                ...data,
                                shippingAddress: {
                                    ...data.shippingAddress,
                                    customer: complaint.shippingInfo.customer?.customer || '',
                                    salesOrg: '',
                                    partner: 'Ship-ToParty',
                                    name1: customer.name1,
                                    name2: '',
                                    street: formik.values.street,
                                    city: formik.values.city,
                                    region: formik.values.region,
                                    postalCode: formik.values.postalCode,
                                    country: formik.values.country,
                                    telephone: complaint.guestInfo.telephone
                                }
                            }))
                    }}/>

                    <CancelButton/>
                </Stack>
            </Grid>
        </form>
    )
}

export default ShippingAddress
